import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import api from '../utils/axios';
import { Text, Icon, Button } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

interface TestMetadataModalProps {
  projectId: string;
  threadId: string;
  onClose: () => void;
}

export const TestMetadataModal: React.FC<TestMetadataModalProps> = ({
  projectId,
  threadId,
  onClose,
}) => {
  const [memory, setMemory] = useState<string>('');
  const [editValue, setEditValue] = useState<string>('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    fetchMemory();
  }, [projectId, threadId]);

  const fetchMemory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(
        `/dev-agent/projects/${projectId}/threads/${threadId}/test-memory`
      );
      const content = response.data.content ?? '';
      setMemory(content);
      setEditValue(content);
      setUpdatedAt(response.data.updated_at ?? null);
    } catch {
      setError('Failed to load testing context');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      await api.put(
        `/dev-agent/projects/${projectId}/threads/${threadId}/test-memory`,
        { content: editValue }
      );
      setMemory(editValue);
      setIsDirty(false);
      // Refresh to get the new updated_at from server
      const response = await api.get(
        `/dev-agent/projects/${projectId}/threads/${threadId}/test-memory`
      );
      setUpdatedAt(response.data.updated_at ?? null);
    } catch {
      setSaveError('Failed to save testing context');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    try {
      setClearing(true);
      setSaveError(null);
      await api.delete(
        `/dev-agent/projects/${projectId}/threads/${threadId}/test-memory`
      );
      setMemory('');
      setEditValue('');
      setUpdatedAt(null);
      setIsDirty(false);
      setConfirmingClear(false);
    } catch {
      setSaveError('Failed to clear testing context');
      setConfirmingClear(false);
    } finally {
      setClearing(false);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditValue(e.target.value);
    setIsDirty(e.target.value !== memory);
    setSaveError(null);
  };

  const formatUpdatedAt = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt shadow-bg-v2-3xl ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-bg-primary box-border flex gap-bg-v2-md items-start p-bg-v2-3xl w-full">
          <div className="flex flex-[1_0_0] gap-bg-v2-xl items-start min-h-px min-w-px">
            <div className="flex flex-[1_0_0] flex-col gap-bg-v2-xxs items-start min-h-px min-w-px whitespace-pre-wrap">
              <Text variant="text-lg" weight="semibold" color="primary" className="w-full flex items-center gap-2">
                Testing Context
              </Text>
              <Text variant="text-sm" weight="regular" color="secondary" className="w-full">
                Context that helps the Testing Agent understand your app
              </Text>
            </div>
          </div>
          <div className="absolute right-3 top-3">
            <Button
              onClick={handleClose}
              variant="ghost"
              size="small"
              label={<Icon name={IconName.Cross} size="md" inheritColor />}
              className="!rounded-full w-8 h-8 !p-0"
            />
          </div>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-bg-v2-xl items-center px-bg-v2-3xl py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="box-border flex flex-col gap-bg-v2-xl items-start px-bg-v2-3xl py-bg-v2-xl w-full flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col gap-3 w-full">
              <div className="h-4 skeleton-shimmer rounded w-1/3" />
              <div className="h-[200px] skeleton-shimmer rounded w-full" />
              <div className="h-4 skeleton-shimmer rounded w-1/4" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 w-full">
              <AlertCircle className="w-12 h-12 text-text-error-primary" />
              <Text variant="text-md" weight="semibold" color="primary">
                Failed to Load
              </Text>
              <Text variant="text-sm" color="secondary" align="center">
                {error}
              </Text>
              <Button
                onClick={fetchMemory}
                variant="primary"
                size="small"
                label="Try Again"
                className="mt-2"
              />
            </div>
          ) : (
            <>
              {/* Info banner */}
              <div className="flex items-start gap-2 p-3 bg-bg-secondary rounded-bg-v2-md w-full border border-border-secondary">
                <Text variant="text-xs" color="secondary">
                  Add anything that helps the Testing Agent — user details and test logins, how the app works, what to focus on, or areas to avoid. Include anything specific to your application that may be useful while testing. The agent also saves useful context here as it learns.
                </Text>
              </div>

              {/* Textarea */}
              <div className="w-full flex flex-col gap-1 flex-1 min-h-[200px]">
                <Text variant="text-xs" weight="medium" color="secondary" className="shrink-0">
                  Context
                </Text>
                <textarea
                  value={editValue}
                  onChange={handleTextareaChange}
                  placeholder="Add context to help the Testing Agent test more accurately — e.g. how key flows work, test logins and environments, what to focus on, and anything it should avoid. Anything you'd tell a new QA teammate on their first day belongs here."
                  className="w-full flex-1 px-3 py-2.5 text-sm border border-border-secondary rounded-bg-v2-md focus:outline-none focus:ring-2 focus:ring-border-brand focus:border-border-brand bg-bg-primary text-text-primary resize-none modern-scrollbar font-mono leading-relaxed shadow-bg-v2-sm"
                />
              </div>

              {/* Last updated */}
              {updatedAt && (
                <Text variant="text-xs" color="tertiary" className="w-full">
                  Last updated: {formatUpdatedAt(updatedAt)}
                </Text>
              )}

              {/* Save Error */}
              {saveError && (
                <div className="flex items-center gap-2 p-2.5 bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-sm w-full">
                  <AlertCircle className="w-4 h-4 text-text-error-primary shrink-0" />
                  <Text variant="text-xs" color="error">
                    {saveError}
                  </Text>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        {!loading && !error && (
          <div className="box-border flex gap-bg-v2-md items-center justify-between px-bg-v2-3xl py-bg-v2-xl w-full border-t border-border-secondary">
            {/* Clear section — shows confirmation inline */}
            {confirmingClear ? (
              <div className="flex items-center gap-2 animate-fade-in">
                <span className="text-xs text-text-error-primary font-medium">Are you sure?</span>
                <Button
                  onClick={handleClear}
                  disabled={clearing}
                  loading={clearing}
                  variant="error"
                  size="small"
                  label="Clear"
                />
                <Button
                  onClick={() => setConfirmingClear(false)}
                  disabled={clearing}
                  variant="outline"
                  size="small"
                  label="Cancel"
                />
              </div>
            ) : (
              <Button
                onClick={() => setConfirmingClear(true)}
                disabled={saving || (!memory && !editValue)}
                variant="outline"
                size="small"
                label="Clear Context"
                className="!text-text-error-primary !border-border-error-subtle hover:!bg-bg-error-primary hover:!text-text-error-primary"
              />
            )}

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={clearing || !isDirty}
              loading={saving}
              variant="primary"
              size="small"
              label="Save Changes"
              className="px-4"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TestMetadataModal;
